"use client"

import { motion } from "framer-motion"
import { Truck, Palette, RotateCcw, MessageSquare } from "lucide-react"
import { Button } from "./ui/button"

const experiences = [
  {
    icon: <Palette className="h-8 w-8" />,
    title: "Styling Assistance",
    description: "Expert guidance to help you create spaces that truly reflect you.",
  },
  {
    icon: <Truck className="h-8 w-8" />,
    title: "Hassle-Free Delivery",
    description: "Seamless shipping, handled with care from our door to yours.",
  },
  {
    icon: <RotateCcw className="h-8 w-8" />,
    title: "Customization",
    description: "Bespoke designs crafted to fit your vision perfectly.",
  },
  {
    icon: <MessageSquare className="h-8 w-8" />,
    title: "Artisanal Brands",
    description: "Curated creations from master craftsmen and boutique makers.",
  },
]

export default function ExperienceSection() {
  return (
    <section className="bg-white pt-8 pb-16 sm:pt-12 sm:pb-20 md:pt-16 md:pb-24">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="grid gap-8 sm:gap-10 md:gap-12 lg:grid-cols-2 lg:items-center">
          <div className="order-2 lg:order-1">
            <motion.h2
              className="mb-4 sm:mb-6 font-serif text-2xl font-light sm:text-3xl md:text-4xl leading-tight"
              initial={{ opacity: 0 }}ˀ
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              White Glove. Doorstep to Dream Space.
            </motion.h2>

            <motion.p
              className="mb-6 sm:mb-8 text-muted-foreground text-base sm:text-lg leading-relaxed"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              We believe luxury extends beyond our products to encompass the entire experience, from browsing to living
              with your new pieces.
            </motion.p>

            <div className="grid gap-4 sm:gap-6 md:grid-cols-2">
              {experiences.map((experience, index) => (
                <motion.div
                  key={index}
                  className="flex flex-col p-4 sm:p-0"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <div className="mb-3 text-amber-700">{experience.icon}</div>
                  <h3 className="mb-2 font-medium text-base sm:text-lg">{experience.title}</h3>
                  <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">{experience.description}</p>
                </motion.div>
              ))}
            </div>

            <motion.div
              className="mt-6 sm:mt-8 text-center lg:text-left"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6 }}
            >
              <button 
                className="inline-flex items-center justify-center px-6 py-3 sm:px-8 sm:py-3 bg-[#f7e8d5] text-stone-800 font-medium transition-colors hover:bg-[#f0dbc4] focus:outline-none focus:ring-2 focus:ring-[#f7e8d5] focus:ring-offset-2 w-full sm:w-auto text-sm sm:text-base"
                style={{ backgroundColor: '#f7e8d5', color: '#292524' }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#f0dbc4'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#f7e8d5'}
                onClick={() => window.location.href = '/contact'}
              >
                Ask Us Anything
              </button>
            </motion.div>
          </div>

          <motion.div
            className="relative aspect-[4/3] w-full order-1 lg:order-2"
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <img
              src="/wgd.png?height=800&width=1000"
              alt="White glove delivery service"
              className="w-full h-full object-cover rounded-lg sm:rounded-xl"
            />
          </motion.div>
        </div>
      </div>
    </section>
  )
} 